// src/pages/logistics/LogisticsPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Plane,
  Hotel,
  Car,
  MapPin,
  Plus,
  Calendar,
  Clock,
  User,
  X,
  CheckCircle,
  AlertCircle,
  Search,
  Filter,
  Download,
  FileText,
  DollarSign,
  Navigation,
  Edit2,
  Trash2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { logisticsService } from '../../services/logistics.service';
import { userService } from '../../services/user.service';
import {
  ProjectLogistics,
  LogisticsType,
  LogisticsStatus,
  HotelInfo,
  FlightInfo,
  RentalCarInfo,
  GroundTransportInfo
} from '../../types/logistics.types';
import { UserProfile } from '../../types/user.types';
import { format, parseISO, isAfter, isBefore } from 'date-fns';
import { toast } from 'react-hot-toast';

const LogisticsPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { selectedProject, projects } = useProject();
  const [logistics, setLogistics] = useState<ProjectLogistics[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLogistics, setSelectedLogistics] = useState<ProjectLogistics | null>(null);
  const [filterType, setFilterType] = useState<LogisticsType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<LogisticsStatus | 'all'>('all');

  const isAdmin = userProfile?.role === 'provider_admin' || userProfile?.role === 'client_admin';

  useEffect(() => {
    loadLogistics();
  }, [currentUser, userProfile, selectedProject]);

  const loadLogistics = async () => {
    if (!currentUser || !userProfile) return;

    setLoading(true);
    try {
      let logisticsList: ProjectLogistics[];

      if (isAdmin) {
        if (selectedProject) {
          logisticsList = await logisticsService.getProjectLogistics(selectedProject.id);
        } else {
          logisticsList = await logisticsService.getLogisticsList();
        }
      } else {
        logisticsList = await logisticsService.getUserLogistics(currentUser.uid);
      }

      setLogistics(logisticsList);
    } catch (error) {
      console.error('Error loading logistics:', error);
      toast.error('Failed to load logistics');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: LogisticsType) => {
    switch (type) {
      case 'flight':
        return <Plane className="h-5 w-5" />;
      case 'hotel':
        return <Hotel className="h-5 w-5" />;
      case 'rental_car':
        return <Car className="h-5 w-5" />;
      case 'ground_transport':
        return <Navigation className="h-5 w-5" />;
      default:
        return <MapPin className="h-5 w-5" />;
    }
  };

  const getTypeColor = (type: LogisticsType) => {
    switch (type) {
      case 'flight':
        return 'bg-secondary-100 text-secondary-600';
      case 'hotel':
        return 'bg-primary-100 text-primary-600';
      case 'rental_car':
        return 'bg-accent-100 text-accent-600';
      case 'ground_transport':
        return 'bg-coral-100 text-coral-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatusBadge = (status: LogisticsStatus) => {
    const styles: Record<LogisticsStatus, string> = {
      pending: 'bg-gray-100 text-gray-700',
      confirmed: 'bg-accent-100 text-accent-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-primary-100 text-primary-700'
    };

    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getLogisticsDateRange = (item: ProjectLogistics) => {
    switch (item.type) {
      case 'hotel':
        return {
          start: item.hotel?.checkInDate,
          end: item.hotel?.checkOutDate
        };
      case 'flight':
        return {
          start: item.flight?.departureDate,
          end: item.flight?.arrivalDate || item.flight?.departureDate
        };
      case 'rental_car':
        return {
          start: item.rentalCar?.pickupDate,
          end: item.rentalCar?.dropoffDate
        };
      case 'ground_transport':
        return {
          start: item.groundTransport?.pickupDate,
          end: item.groundTransport?.pickupDate
        };
      default:
        return {
          start: item.otherDetails?.startDate,
          end: item.otherDetails?.endDate
        };
    }
  };

  const filteredLogistics = logistics.filter(item => {
    if (filterType !== 'all' && item.type !== filterType) return false;
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    return true;
  });

  // Sort by date
  const sortedLogistics = [...filteredLogistics].sort((a, b) => {
    const dateA = getLogisticsDateRange(a).start;
    const dateB = getLogisticsDateRange(b).start;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return new Date(dateA).getTime() - new Date(dateB).getTime();
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Travel & Logistics</h1>
          <p className="text-gray-500 mt-1">
            {isAdmin ? 'Manage travel arrangements for your team' : 'View your travel arrangements'}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-200"
          >
            <Plus className="h-5 w-5" />
            Add Logistics
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex gap-2 flex-1">
            {['all', 'flight', 'hotel', 'rental_car', 'ground_transport'].map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type as any)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterType === type
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type === 'all' && 'All'}
                {type === 'flight' && <><Plane className="h-4 w-4" /> Flights</>}
                {type === 'hotel' && <><Hotel className="h-4 w-4" /> Hotels</>}
                {type === 'rental_car' && <><Car className="h-4 w-4" /> Cars</>}
                {type === 'ground_transport' && <><Navigation className="h-4 w-4" /> Ground</>}
              </button>
            ))}
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Logistics List */}
      {sortedLogistics.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Plane className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">No logistics found</h3>
          <p className="text-gray-500">
            {isAdmin
              ? 'Add travel arrangements to get started.'
              : 'No travel arrangements assigned to you yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedLogistics.map((item) => {
            const dateRange = getLogisticsDateRange(item);
            const project = projects.find(p => p.id === item.projectId);

            return (
              <div
                key={item.id}
                className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow ${isAdmin ? 'cursor-pointer' : ''}`}
                onClick={() => {
                  if (isAdmin) {
                    setSelectedLogistics(item);
                    setShowEditModal(true);
                  }
                }}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${getTypeColor(item.type)}`}>
                    {getTypeIcon(item.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-800">{item.title}</h3>
                        {item.description && (
                          <p className="text-sm text-gray-500 mt-0.5">{item.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(item.status)}
                        {isAdmin && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLogistics(item);
                              setShowEditModal(true);
                            }}
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Type-specific details */}
                    <div className="grid sm:grid-cols-2 gap-4 text-sm">
                      {item.type === 'flight' && item.flight && (
                        <>
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            {item.flight.departureAirport} → {item.flight.arrivalAirport}
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {format(parseISO(item.flight.departureDate), 'MMM d, yyyy')}
                            {item.flight.departureTime && ` at ${item.flight.departureTime}`}
                          </div>
                          {item.flight.airline && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <Plane className="h-4 w-4 text-gray-400" />
                              {item.flight.airline} {item.flight.flightNumber}
                            </div>
                          )}
                        </>
                      )}

                      {item.type === 'hotel' && item.hotel && (
                        <>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Hotel className="h-4 w-4 text-gray-400" />
                            {item.hotel.hotelName}
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            {item.hotel.address}
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {format(parseISO(item.hotel.checkInDate), 'MMM d')} - {format(parseISO(item.hotel.checkOutDate), 'MMM d, yyyy')}
                          </div>
                          {item.hotel.confirmationNumber && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <FileText className="h-4 w-4 text-gray-400" />
                              Conf: {item.hotel.confirmationNumber}
                            </div>
                          )}
                        </>
                      )}

                      {item.type === 'rental_car' && item.rentalCar && (
                        <>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Car className="h-4 w-4 text-gray-400" />
                            {item.rentalCar.company} - {item.rentalCar.vehicleType}
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            {item.rentalCar.pickupLocation}
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {format(parseISO(item.rentalCar.pickupDate), 'MMM d')} - {format(parseISO(item.rentalCar.dropoffDate), 'MMM d, yyyy')}
                          </div>
                        </>
                      )}

                      {item.type === 'ground_transport' && item.groundTransport && (
                        <>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Navigation className="h-4 w-4 text-gray-400" />
                            {item.groundTransport.serviceType}
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            {item.groundTransport.pickupLocation} → {item.groundTransport.dropoffLocation}
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            {format(parseISO(item.groundTransport.pickupDate), 'MMM d, yyyy')} at {item.groundTransport.pickupTime}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        {item.assignedToName && (
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {item.assignedToName}
                          </span>
                        )}
                        {project && (
                          <span className="flex items-center gap-1">
                            <FileText className="h-4 w-4" />
                            {project.name}
                          </span>
                        )}
                        {item.totalCost && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-4 w-4" />
                            ${item.totalCost.toFixed(2)}
                          </span>
                        )}
                      </div>

                      {item.attachments && item.attachments.length > 0 && (
                        <a
                          href={item.attachments[0].url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700"
                        >
                          <Download className="h-4 w-4" />
                          View Confirmation
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateLogisticsModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadLogistics();
          }}
          projects={projects}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedLogistics && (
        <EditLogisticsModal
          logistics={selectedLogistics}
          onClose={() => {
            setShowEditModal(false);
            setSelectedLogistics(null);
          }}
          onUpdated={() => {
            setShowEditModal(false);
            setSelectedLogistics(null);
            loadLogistics();
          }}
          onDeleted={() => {
            setShowEditModal(false);
            setSelectedLogistics(null);
            loadLogistics();
          }}
          projects={projects}
        />
      )}
    </div>
  );
};

// Create Logistics Modal
interface CreateLogisticsModalProps {
  onClose: () => void;
  onCreated: () => void;
  projects: any[];
}

const CreateLogisticsModal: React.FC<CreateLogisticsModalProps> = ({ onClose, onCreated, projects }) => {
  const { currentUser, userProfile } = useAuth();
  const [type, setType] = useState<LogisticsType>('hotel');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [status, setStatus] = useState<LogisticsStatus>('pending');
  const [totalCost, setTotalCost] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Notes field (common to all)
  const [notes, setNotes] = useState('');

  // Type-specific fields - Hotel
  const [hotelName, setHotelName] = useState('');
  const [hotelAddress, setHotelAddress] = useState('');
  const [checkInDate, setCheckInDate] = useState('');
  const [checkInTime, setCheckInTime] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');
  const [hotelConfirmation, setHotelConfirmation] = useState('');

  // Flight fields
  const [airline, setAirline] = useState('');
  const [flightNumber, setFlightNumber] = useState('');
  const [flightConfirmation, setFlightConfirmation] = useState('');
  const [departureAirport, setDepartureAirport] = useState('');
  const [arrivalAirport, setArrivalAirport] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [departureTime, setDepartureTime] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');

  // Rental car fields
  const [carCompany, setCarCompany] = useState('');
  const [carConfirmation, setCarConfirmation] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [pickupLocation, setPickupLocation] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [dropoffLocation, setDropoffLocation] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');
  const [dropoffTime, setDropoffTime] = useState('');

  const isProviderRole = userProfile?.role === 'provider_admin' || userProfile?.role === 'provider_user';

  useEffect(() => {
    loadUsers();
  }, [userProfile]);

  const loadUsers = async () => {
    if (!userProfile) return;
    try {
      // Provider users can see all users, others only see their organization
      if (isProviderRole) {
        const userList = await userService.getAllUsers();
        setUsers(userList);
      } else if (userProfile.organizationId) {
        const userList = await userService.getOrganizationUsers(userProfile.organizationId);
        setUsers(userList);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile || !currentUser) return;

    setSubmitting(true);
    try {
      const selectedUser = users.find(u => u.uid === assignedTo);
      const selectedProject = projects.find(p => p.id === projectId);

      // For provider users, use 'provider' as organizationId
      const orgId = isProviderRole ? 'provider' : (userProfile.organizationId || 'provider');

      // Build base data - only include defined values
      const baseData: Record<string, any> = {
        organizationId: orgId,
        title,
        type,
        status,
        createdBy: currentUser.uid,
        createdByName: userProfile.name || userProfile.email || 'Unknown',
        totalCost: totalCost ? parseFloat(totalCost) : 0
      };

      // Only add optional fields if they have values
      if (projectId) {
        baseData.projectId = projectId;
        baseData.projectName = selectedProject?.name || '';
      }
      if (description) baseData.description = description;
      if (assignedTo) {
        baseData.assignedTo = assignedTo;
        baseData.assignedToName = selectedUser?.name || selectedUser?.email || '';
      }
      if (notes) baseData.notes = notes;

      // Build type-specific data - only include defined values
      if (type === 'hotel') {
        const hotelData: Record<string, any> = {
          hotelName,
          address: hotelAddress,
          checkInDate,
          checkOutDate
        };
        if (checkInTime) hotelData.checkInTime = checkInTime;
        if (checkOutTime) hotelData.checkOutTime = checkOutTime;
        if (hotelConfirmation) hotelData.confirmationNumber = hotelConfirmation;
        if (notes) hotelData.notes = notes;
        baseData.hotel = hotelData;
      } else if (type === 'flight') {
        const flightData: Record<string, any> = {
          departureAirport,
          arrivalAirport,
          departureDate,
          arrivalDate: arrivalDate || departureDate
        };
        if (airline) flightData.airline = airline;
        if (flightNumber) flightData.flightNumber = flightNumber;
        if (flightConfirmation) flightData.confirmationCode = flightConfirmation;
        if (departureTime) flightData.departureTime = departureTime;
        if (arrivalTime) flightData.arrivalTime = arrivalTime;
        if (notes) flightData.notes = notes;
        baseData.flight = flightData;
      } else if (type === 'rental_car') {
        const carData: Record<string, any> = {
          company: carCompany,
          pickupLocation,
          pickupDate,
          dropoffLocation: dropoffLocation || pickupLocation,
          dropoffDate
        };
        if (carConfirmation) carData.confirmationNumber = carConfirmation;
        if (vehicleType) carData.vehicleType = vehicleType;
        if (pickupTime) carData.pickupTime = pickupTime;
        if (dropoffTime) carData.dropoffTime = dropoffTime;
        if (notes) carData.notes = notes;
        baseData.rentalCar = carData;
      } else if (type === 'ground_transport') {
        const groundData: Record<string, any> = {
          serviceType: 'ground_transport',
          pickupLocation: pickupLocation || '',
          pickupDate: pickupDate || '',
          pickupTime: pickupTime || '',
          dropoffLocation: dropoffLocation || ''
        };
        if (notes) groundData.notes = notes;
        baseData.groundTransport = groundData;
      }

      await logisticsService.createLogistics(baseData as any);

      toast.success('Logistics created successfully');
      onCreated();
    } catch (error) {
      console.error('Error creating logistics:', error);
      toast.error('Failed to create logistics');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">Add Logistics</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <div className="flex gap-2">
              {[
                { id: 'hotel', icon: Hotel, label: 'Hotel' },
                { id: 'flight', icon: Plane, label: 'Flight' },
                { id: 'rental_car', icon: Car, label: 'Car' },
                { id: 'ground_transport', icon: Navigation, label: 'Ground' }
              ].map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setType(id as LogisticsType)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    type === id
                      ? 'bg-primary-100 text-primary-700 border-2 border-primary-300'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Common Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., Flight to Chicago"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select project</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign To <span className="text-gray-400 font-normal">(optional)</span></label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Unassigned</option>
                {users.map(u => (
                  <option key={u.uid} value={u.uid}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Type-specific fields */}
          {type === 'hotel' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name</label>
                <input
                  type="text"
                  value={hotelName}
                  onChange={(e) => setHotelName(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={hotelAddress}
                  onChange={(e) => setHotelAddress(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmation #</label>
                <input
                  type="text"
                  value={hotelConfirmation}
                  onChange={(e) => setHotelConfirmation(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., ABC123456"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date</label>
                  <input
                    type="date"
                    value={checkInDate}
                    onChange={(e) => setCheckInDate(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Time</label>
                  <input
                    type="time"
                    value={checkInTime}
                    onChange={(e) => setCheckInTime(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date</label>
                  <input
                    type="date"
                    value={checkOutDate}
                    onChange={(e) => setCheckOutDate(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Time</label>
                  <input
                    type="time"
                    value={checkOutTime}
                    onChange={(e) => setCheckOutTime(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </>
          )}

          {type === 'flight' && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Airline</label>
                  <input
                    type="text"
                    value={airline}
                    onChange={(e) => setAirline(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Delta"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Flight #</label>
                  <input
                    type="text"
                    value={flightNumber}
                    onChange={(e) => setFlightNumber(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="DL1234"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirmation #</label>
                  <input
                    type="text"
                    value={flightConfirmation}
                    onChange={(e) => setFlightConfirmation(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="ABC123"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Departure Airport</label>
                  <input
                    type="text"
                    value={departureAirport}
                    onChange={(e) => setDepartureAirport(e.target.value)}
                    required
                    placeholder="ORD"
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Airport</label>
                  <input
                    type="text"
                    value={arrivalAirport}
                    onChange={(e) => setArrivalAirport(e.target.value)}
                    required
                    placeholder="LAX"
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Departure Date</label>
                  <input
                    type="date"
                    value={departureDate}
                    onChange={(e) => setDepartureDate(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Departure Time</label>
                  <input
                    type="time"
                    value={departureTime}
                    onChange={(e) => setDepartureTime(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Date</label>
                  <input
                    type="date"
                    value={arrivalDate}
                    onChange={(e) => setArrivalDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Time</label>
                  <input
                    type="time"
                    value={arrivalTime}
                    onChange={(e) => setArrivalTime(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </>
          )}

          {type === 'rental_car' && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={carCompany}
                    onChange={(e) => setCarCompany(e.target.value)}
                    required
                    placeholder="Enterprise"
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                  <input
                    type="text"
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                    placeholder="SUV, Sedan"
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirmation #</label>
                  <input
                    type="text"
                    value={carConfirmation}
                    onChange={(e) => setCarConfirmation(e.target.value)}
                    placeholder="ABC123"
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Location</label>
                  <input
                    type="text"
                    value={pickupLocation}
                    onChange={(e) => setPickupLocation(e.target.value)}
                    required
                    placeholder="Airport, Address..."
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Drop-off Location</label>
                  <input
                    type="text"
                    value={dropoffLocation}
                    onChange={(e) => setDropoffLocation(e.target.value)}
                    placeholder="Same as pickup if blank"
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Date</label>
                  <input
                    type="date"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Time</label>
                  <input
                    type="time"
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Drop-off Date</label>
                  <input
                    type="date"
                    value={dropoffDate}
                    onChange={(e) => setDropoffDate(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Drop-off Time</label>
                  <input
                    type="time"
                    value={dropoffTime}
                    onChange={(e) => setDropoffTime(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </>
          )}

          {/* Cost & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={totalCost}
                  onChange={(e) => setTotalCost(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-7 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as LogisticsStatus)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Additional notes, special instructions, etc."
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Add Logistics
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Logistics Modal
interface EditLogisticsModalProps {
  logistics: ProjectLogistics;
  onClose: () => void;
  onUpdated: () => void;
  onDeleted: () => void;
  projects: any[];
}

const EditLogisticsModal: React.FC<EditLogisticsModalProps> = ({
  logistics,
  onClose,
  onUpdated,
  onDeleted,
  projects
}) => {
  const { currentUser, userProfile } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Form state
  const [title, setTitle] = useState(logistics.title || '');
  const [description, setDescription] = useState(logistics.description || '');
  const [projectId, setProjectId] = useState(logistics.projectId || '');
  const [assignedTo, setAssignedTo] = useState(logistics.assignedTo || '');
  const [status, setStatus] = useState<LogisticsStatus>(logistics.status);
  const [totalCost, setTotalCost] = useState(logistics.totalCost?.toString() || '');
  const [notes, setNotes] = useState(logistics.notes || '');

  const isProviderRole = userProfile?.role === 'provider_admin' || userProfile?.role === 'provider_user';

  useEffect(() => {
    loadUsers();
  }, [userProfile]);

  const loadUsers = async () => {
    if (!userProfile) return;
    try {
      if (isProviderRole) {
        const userList = await userService.getAllUsers();
        setUsers(userList);
      } else if (userProfile.organizationId) {
        const userList = await userService.getOrganizationUsers(userProfile.organizationId);
        setUsers(userList);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userProfile) return;

    setSubmitting(true);
    try {
      const selectedUser = users.find(u => u.uid === assignedTo);
      const selectedProject = projects.find(p => p.id === projectId);

      const updates: Record<string, any> = {
        title,
        status,
        totalCost: totalCost ? parseFloat(totalCost) : 0
      };

      if (description) updates.description = description;
      else updates.description = null;

      if (projectId) {
        updates.projectId = projectId;
        updates.projectName = selectedProject?.name || '';
      } else {
        updates.projectId = null;
        updates.projectName = null;
      }

      if (assignedTo) {
        updates.assignedTo = assignedTo;
        updates.assignedToName = selectedUser?.name || selectedUser?.email || '';
      } else {
        updates.assignedTo = null;
        updates.assignedToName = null;
      }

      if (notes) updates.notes = notes;
      else updates.notes = null;

      await logisticsService.updateLogistics(logistics.id, updates);
      toast.success('Logistics updated successfully');
      onUpdated();
    } catch (error) {
      console.error('Error updating logistics:', error);
      toast.error('Failed to update logistics');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await logisticsService.deleteLogistics(logistics.id);
      toast.success('Logistics deleted');
      onDeleted();
    } catch (error) {
      console.error('Error deleting logistics:', error);
      toast.error('Failed to delete logistics');
    } finally {
      setDeleting(false);
    }
  };

  const getTypeLabel = (type: LogisticsType) => {
    const labels: Record<LogisticsType, string> = {
      hotel: 'Hotel',
      flight: 'Flight',
      rental_car: 'Rental Car',
      ground_transport: 'Ground Transport',
      other: 'Other'
    };
    return labels[type] || type;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Edit Logistics</h2>
              <p className="text-sm text-gray-500 mt-1">{getTypeLabel(logistics.type)}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Project & Assigned User */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">No project</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Unassigned</option>
                {users.map(u => (
                  <option key={u.uid} value={u.uid}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Status & Cost */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as LogisticsStatus)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={totalCost}
                  onChange={(e) => setTotalCost(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-7 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Type-specific info (read-only) */}
          {logistics.type === 'hotel' && logistics.hotel && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Hotel Details</h4>
              <div className="text-sm text-gray-600">
                <p><strong>Hotel:</strong> {logistics.hotel.hotelName}</p>
                <p><strong>Address:</strong> {logistics.hotel.address}</p>
                <p><strong>Check-in:</strong> {logistics.hotel.checkInDate}</p>
                <p><strong>Check-out:</strong> {logistics.hotel.checkOutDate}</p>
                {logistics.hotel.confirmationNumber && (
                  <p><strong>Confirmation:</strong> {logistics.hotel.confirmationNumber}</p>
                )}
              </div>
            </div>
          )}

          {logistics.type === 'flight' && logistics.flight && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Flight Details</h4>
              <div className="text-sm text-gray-600">
                <p><strong>Route:</strong> {logistics.flight.departureAirport} → {logistics.flight.arrivalAirport}</p>
                <p><strong>Date:</strong> {logistics.flight.departureDate}</p>
                {logistics.flight.airline && <p><strong>Airline:</strong> {logistics.flight.airline} {logistics.flight.flightNumber}</p>}
                {logistics.flight.confirmationCode && <p><strong>Confirmation:</strong> {logistics.flight.confirmationCode}</p>}
              </div>
            </div>
          )}

          {logistics.type === 'rental_car' && logistics.rentalCar && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Rental Car Details</h4>
              <div className="text-sm text-gray-600">
                <p><strong>Company:</strong> {logistics.rentalCar.company}</p>
                <p><strong>Pickup:</strong> {logistics.rentalCar.pickupLocation} on {logistics.rentalCar.pickupDate}</p>
                <p><strong>Drop-off:</strong> {logistics.rentalCar.dropoffLocation} on {logistics.rentalCar.dropoffDate}</p>
                {logistics.rentalCar.confirmationNumber && <p><strong>Confirmation:</strong> {logistics.rentalCar.confirmationNumber}</p>}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-2xl">
            <div className="bg-white rounded-xl p-6 m-4 max-w-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Logistics?</h3>
              <p className="text-gray-600 text-sm mb-4">
                This will permanently delete this logistics entry and any associated expense records.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogisticsPage;
